'use client';

import { memo } from 'react';
import clsx from 'clsx';
import { GlowNotificationToast } from '@headless-workspace/glow-ds';
import { ComponentMemo } from '@headless-workspace/typings';
import { createPortal } from 'react-dom';
import { useIsClient } from 'usehooks-ts';
import { useToastActionsContext, useToastStateContext } from '../../providers';
import { useToastContainerPositioning } from './UIToastContainerHooks';

type UIToastContainerProps = {
    crossIconAccessibilityTitle?: string;
};

export const UIToastContainer: ComponentMemo<UIToastContainerProps> = memo(({ crossIconAccessibilityTitle }) => {
    const { closeToast } = useToastActionsContext();
    const { toast } = useToastStateContext();
    const isClient = useIsClient();

    const { container, style, zIndex, classNames, animationClass } = useToastContainerPositioning(toast);

    if (!container || !isClient) {
        return null;
    }

    return createPortal(
        <div
            style={style}
            className={clsx(
                'fixed tablet:absolute desktop:fixed',
                'bottom-0 tablet:bottom-auto left-0 tablet:left-auto tablet:right-0 desktop:right-0',
                'flex flex-col tablet:justify-center gap-1',
                'w-fullViewport tablet:w-auto tablet:max-w-[20rem] desktop:max-w-[27.5rem]',
                'px-1 mb-1 tablet:mt-1',
                zIndex,
                classNames,
            )}
        >
            {toast && (
                <GlowNotificationToast
                    label={{ text: toast.label }}
                    toastType={toast.type}
                    onClose={toast.options?.canClose ? closeToast : undefined}
                    className={animationClass}
                    crossIconAccessibilityTitle={crossIconAccessibilityTitle}
                />
            )}
        </div>,
        container,
    );
});

UIToastContainer.displayName = 'UIToastContainer';
